import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert, AlertIcon, Badge, Box, Button, Flex, FormControl, FormLabel,
  Heading, HStack, Icon, IconButton, Input, Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalFooter, ModalHeader, ModalOverlay, Spinner, Switch,
  Table, Tbody, Td, Text, Th, Thead, Tr, Tooltip, useDisclosure, VStack,
  Code, useClipboard,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiKey, FiCopy, FiCheck } from 'react-icons/fi';

export default function APIKeys({ token, apiBase }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({ name: '', description: '', is_active: true });
  const [newKey, setNewKey] = useState('');
  const [saving, setSaving] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { hasCopied, onCopy } = useClipboard(newKey);

  const headers = { Authorization: `Token ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${apiBase}/api/v1/api-keys/`, { headers })
      .then(r => r.json())
      .then(data => setKeys(data.results || []))
      .catch(() => setError('Failed to load API keys'))
      .finally(() => setLoading(false));
  }, [apiBase, token]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${apiBase}/api/v1/api-keys/`, {
        method: 'POST', headers, body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (res.ok) {
        setNewKey(data.key);
        load();
      }
    } catch {
      setError('Network error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    await fetch(`${apiBase}/api/v1/api-keys/${id}/`, { method: 'DELETE', headers });
    load();
  };

  return (
    <Box p={8}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" color="gray.800">API Keys</Heading>
          <Text color="gray.500" mt={1}>Manage service authentication keys</Text>
        </Box>
        <Button leftIcon={<Icon as={FiPlus} />} colorScheme="blue" onClick={() => { setNewKey(''); onOpen(); }}>
          Create Key
        </Button>
      </Flex>

      {error && <Alert status="error" mb={4}><AlertIcon />{error}</Alert>}
      {loading && <Spinner size="xl" color="blue.500" />}

      {newKey && (
        <Alert status="success" mb={6} rounded="lg" flexDirection="column" alignItems="flex-start">
          <AlertIcon />
          <Text fontWeight="bold" mb={2}>New API Key created — copy it now, it won't be shown again.</Text>
          <HStack>
            <Code p={2} rounded="md" fontSize="sm" wordBreak="break-all">{newKey}</Code>
            <IconButton icon={hasCopied ? <FiCheck /> : <FiCopy />} onClick={onCopy} size="sm" colorScheme="green" aria-label="Copy" />
          </HStack>
        </Alert>
      )}

      {!loading && keys.length > 0 && (
        <Box bg="white" rounded="xl" shadow="sm" overflow="hidden">
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Name</Th>
                <Th>Description</Th>
                <Th>Status</Th>
                <Th>Created</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {keys.map(k => (
                <Tr key={k.id} _hover={{ bg: 'gray.50' }}>
                  <Td fontWeight="semibold">{k.name}</Td>
                  <Td color="gray.500" fontSize="sm">{k.description || '—'}</Td>
                  <Td>
                    <Badge colorScheme={k.is_active ? 'green' : 'gray'}>
                      {k.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </Td>
                  <Td fontSize="sm" color="gray.500">
                    {new Date(k.created_at).toLocaleDateString()}
                  </Td>
                  <Td>
                    <Tooltip label="Delete">
                      <IconButton
                        icon={<FiTrash2 />} size="sm" variant="ghost" colorScheme="red"
                        onClick={() => handleDelete(k.id)} aria-label="Delete"
                      />
                    </Tooltip>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create API Key</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Name</FormLabel>
                <Input
                  value={formData.name}
                  onChange={e => setFormData(f => ({ ...f, name: e.target.value }))}
                  placeholder="Dashboard service"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Input
                  value={formData.description}
                  onChange={e => setFormData(f => ({ ...f, description: e.target.value }))}
                  placeholder="Optional description"
                />
              </FormControl>
              <FormControl>
                <Flex justify="space-between" align="center">
                  <FormLabel mb={0}>Active</FormLabel>
                  <Switch
                    isChecked={formData.is_active}
                    onChange={e => setFormData(f => ({ ...f, is_active: e.target.checked }))}
                    colorScheme="green"
                  />
                </Flex>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="blue" isLoading={saving} onClick={handleCreate}>Create</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
